import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Badge,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Send as SendIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Notifications as NotificationIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { atmCardService } from '../services/atmCardService';

const AtmCardManagement = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Form states
  const [updateForm, setUpdateForm] = useState({
    status: '',
    trackingNumber: '',
    courierService: '',
    estimatedDeliveryDate: '',
    adminNotes: '',
  });
  const [notificationForm, setNotificationForm] = useState({
    type: 'status_update',
    message: '',
    sendEmail: true,
    sendSms: true,
  });

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await atmCardService.getAllRequests();
      setRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
      showSnackbar('Failed to fetch requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setViewDialogOpen(true);
  };

  const handleUpdateRequest = (request) => {
    setSelectedRequest(request);
    setUpdateForm({
      status: request.status,
      trackingNumber: request.delivery?.trackingNumber || '',
      courierService: request.delivery?.courierService || '',
      estimatedDeliveryDate: request.delivery?.estimatedDeliveryDate || '',
      adminNotes: request.adminNotes || '',
    });
    setUpdateDialogOpen(true);
  };

  const handleSendNotification = (request) => {
    setSelectedRequest(request);
    setNotificationForm({
      type: 'status_update',
      message: `Your ATM card request (${request.requestId}) status has been updated to ${request.status}`,
      sendEmail: true,
      sendSms: true,
    });
    setNotificationDialogOpen(true);
  };

  const handleUpdateSubmit = async () => {
    try {
      await atmCardService.updateRequest(selectedRequest._id, updateForm);
      showSnackbar('Request updated successfully', 'success');
      setUpdateDialogOpen(false);
      fetchRequests();
    } catch (error) {
      console.error('Error updating request:', error);
      showSnackbar('Failed to update request', 'error');
    }
  };

  const handleNotificationSubmit = async () => {
    try {
      await atmCardService.sendNotification(selectedRequest._id, notificationForm);
      showSnackbar('Notification sent successfully', 'success');
      setNotificationDialogOpen(false);
    } catch (error) {
      console.error('Error sending notification:', error);
      showSnackbar('Failed to send notification', 'error');
    }
  };


  const filteredRequests = requests.filter(request => {
    const matchesSearch = request.requestId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         request.userId?.fullname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         request.userId?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getTabCounts = () => {
    const counts = {
      all: requests.length,
      pending_payment: requests.filter(r => r.status === 'pending_payment').length,
      processing: requests.filter(r => ['payment_completed', 'processing'].includes(r.status)).length,
      shipped: requests.filter(r => ['shipped', 'delivered'].includes(r.status)).length,
      completed: requests.filter(r => ['card_connected', 'completed'].includes(r.status)).length,
    };
    return counts;
  };

  const tabCounts = getTabCounts();

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        ATM Card Management
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        Monitor and manage ATM card requests
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <ViewIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">{tabCounts.all}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Requests
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                  <EditIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">{tabCounts.processing}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Processing
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                  <ShippingIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">{tabCounts.shipped}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Shipped/Delivered
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <CheckIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">{tabCounts.completed}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by request ID, name, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status Filter"
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="pending_payment">Pending Payment</MenuItem>
                  <MenuItem value="payment_completed">Payment Completed</MenuItem>
                  <MenuItem value="processing">Processing</MenuItem>
                  <MenuItem value="shipped">Shipped</MenuItem>
                  <MenuItem value="delivered">Delivered</MenuItem>
                  <MenuItem value="card_connected">Card Connected</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchRequests}
                fullWidth
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
            <Tab label={`All (${tabCounts.all})`} />
            <Tab label={`Pending Payment (${tabCounts.pending_payment})`} />
            <Tab label={`Processing (${tabCounts.processing})`} />
            <Tab label={`Shipped (${tabCounts.shipped})`} />
            <Tab label={`Completed (${tabCounts.completed})`} />
          </Tabs>
        </Box>

        <TabPanel value={currentTab} index={0}>
          <RequestTable
            requests={filteredRequests}
            onView={handleViewRequest}
            onUpdate={handleUpdateRequest}
            onNotify={handleSendNotification}
            loading={loading}
          />
        </TabPanel>
        <TabPanel value={currentTab} index={1}>
          <RequestTable
            requests={filteredRequests.filter(r => r.status === 'pending_payment')}
            onView={handleViewRequest}
            onUpdate={handleUpdateRequest}
            onNotify={handleSendNotification}
            loading={loading}
          />
        </TabPanel>
        <TabPanel value={currentTab} index={2}>
          <RequestTable
            requests={filteredRequests.filter(r => ['payment_completed', 'processing'].includes(r.status))}
            onView={handleViewRequest}
            onUpdate={handleUpdateRequest}
            onNotify={handleSendNotification}
            loading={loading}
          />
        </TabPanel>
        <TabPanel value={currentTab} index={3}>
          <RequestTable
            requests={filteredRequests.filter(r => ['shipped', 'delivered'].includes(r.status))}
            onView={handleViewRequest}
            onUpdate={handleUpdateRequest}
            onNotify={handleSendNotification}
            loading={loading}
          />
        </TabPanel>
        <TabPanel value={currentTab} index={4}>
          <RequestTable
            requests={filteredRequests.filter(r => ['card_connected', 'completed'].includes(r.status))}
            onView={handleViewRequest}
            onUpdate={handleUpdateRequest}
            onNotify={handleSendNotification}
            loading={loading}
          />
        </TabPanel>
      </Card>

      {/* View Request Dialog */}
      <ViewRequestDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        request={selectedRequest}
      />

      {/* Update Request Dialog */}
      <UpdateRequestDialog
        open={updateDialogOpen}
        onClose={() => setUpdateDialogOpen(false)}
        request={selectedRequest}
        form={updateForm}
        setForm={setUpdateForm}
        onSubmit={handleUpdateSubmit}
      />

      {/* Send Notification Dialog */}
      <NotificationDialog
        open={notificationDialogOpen}
        onClose={() => setNotificationDialogOpen(false)}
        request={selectedRequest}
        form={notificationForm}
        setForm={setNotificationForm}
        onSubmit={handleNotificationSubmit}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Helper functions for status display
const getStatusColor = (status) => {
  const colors = {
    pending_payment: 'warning',
    payment_completed: 'info',
    processing: 'primary',
    shipped: 'secondary',
    delivered: 'success',
    card_connected: 'success',
    completed: 'success',
    cancelled: 'error',
    failed: 'error',
  };
  return colors[status] || 'default';
};

const getStatusIcon = (status) => {
  const icons = {
    pending_payment: <CancelIcon />,
    payment_completed: <CheckIcon />,
    processing: <EditIcon />,
    shipped: <ShippingIcon />,
    delivered: <CheckIcon />,
    card_connected: <CheckIcon />,
    completed: <CheckIcon />,
    cancelled: <CancelIcon />,
    failed: <CancelIcon />,
  };
  return icons[status] || <ViewIcon />;
};

// Request Table Component
const RequestTable = ({ requests, onView, onUpdate, onNotify, loading }) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Loading requests...</Typography>
      </Box>
    );
  }

  if (requests.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography color="text.secondary">No requests found</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Request ID</TableCell>
            <TableCell>Customer</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Payment</TableCell>
            <TableCell>Delivery Address</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {requests.map((request) => (
            <TableRow key={request._id}>
              <TableCell>
                <Typography variant="body2" fontWeight="bold">
                  {request.requestId}
                </Typography>
              </TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {request.userId?.fullname || 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {request.userId?.email || 'N/A'}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  label={request.status?.replace('_', ' ').toUpperCase()}
                  color={getStatusColor(request.status)}
                  size="small"
                  icon={getStatusIcon(request.status)}
                />
              </TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2">
                    ₦{request.payment?.totalAmount?.toLocaleString() || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {request.payment?.paymentMethod || 'N/A'}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {request.delivery?.deliveryAddress?.city}, {request.delivery?.deliveryAddress?.state}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {new Date(request.createdAt).toLocaleDateString()}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => onView(request)}>
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Update Status">
                    <IconButton size="small" onClick={() => onUpdate(request)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Send Notification">
                    <IconButton size="small" onClick={() => onNotify(request)}>
                      <NotificationIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// View Request Dialog Component
const ViewRequestDialog = ({ open, onClose, request }) => {
  if (!request) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        }
      }}
    >
      {/* Custom Header */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        p: 3,
        borderRadius: '12px 12px 0 0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2 }}>
              <ViewIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Request Details
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {request.requestId}
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={onClose} 
            sx={{ 
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            }}
          >
            <CancelIcon />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Customer Information
              </Typography>
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {request.userId?.fullname || 'N/A'}
                </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Email
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {request.userId?.email || 'N/A'}
                </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Phone
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {request.userId?.phone || 'N/A'}
                </Typography>
            </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Payment Information
              </Typography>
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Total Amount
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  ₦{request.payment?.totalAmount?.toLocaleString() || 0}
                </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Payment Method
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {request.payment?.paymentMethod || 'N/A'}
                </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Payment Status
                </Typography>
                <Chip 
                  label={request.payment?.paymentStatus || 'N/A'} 
                  color={request.payment?.paymentStatus === 'completed' ? 'success' : 'warning'}
                  size="small"
                />
            </Box>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Delivery Address
              </Typography>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'grey.50', 
                borderRadius: 2,
                border: '1px solid #f0f0f0'
              }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {request.delivery?.deliveryAddress?.line1}
                {request.delivery?.deliveryAddress?.line2 && `, ${request.delivery.deliveryAddress.line2}`}
              </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {request.delivery?.deliveryAddress?.city}, {request.delivery?.deliveryAddress?.state}
              </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {request.delivery?.deliveryAddress?.country} {request.delivery?.deliveryAddress?.postalCode}
              </Typography>
            </Box>
            </Card>
          </Grid>
          {request.delivery?.trackingNumber && (
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Delivery Information
                </Typography>
              <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Tracking Number
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                    {request.delivery.trackingNumber}
                  </Typography>
              </Box>
              {request.delivery.courierService && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Courier Service
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {request.delivery.courierService}
                    </Typography>
                </Box>
              )}
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button 
          onClick={onClose} 
          variant="contained"
          sx={{ 
            borderRadius: 2,
            px: 3,
            py: 1,
            textTransform: 'none',
            fontWeight: 'bold'
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Update Request Dialog Component
const UpdateRequestDialog = ({ open, onClose, request, form, setForm, onSubmit }) => {
  if (!request) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        }
      }}
    >
      {/* Custom Header */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        color: 'white',
        p: 3,
        borderRadius: '12px 12px 0 0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2 }}>
              <EditIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Update Request
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {request.requestId}
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={onClose} 
            sx={{ 
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            }}
          >
            <CancelIcon />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Card sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                label="Status"
                  sx={{ borderRadius: 2 }}
              >
                <MenuItem value="pending_payment">Pending Payment</MenuItem>
                <MenuItem value="payment_completed">Payment Completed</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="shipped">Shipped</MenuItem>
                <MenuItem value="delivered">Delivered</MenuItem>
                <MenuItem value="card_connected">Card Connected</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Tracking Number"
              value={form.trackingNumber}
              onChange={(e) => setForm({ ...form, trackingNumber: e.target.value })}
                sx={{ 
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Courier Service"
              value={form.courierService}
              onChange={(e) => setForm({ ...form, courierService: e.target.value })}
                sx={{ 
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Estimated Delivery Date"
              type="date"
              value={form.estimatedDeliveryDate}
              onChange={(e) => setForm({ ...form, estimatedDeliveryDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Admin Notes"
              value={form.adminNotes}
              onChange={(e) => setForm({ ...form, adminNotes: e.target.value })}
                placeholder="Add any additional notes or comments..."
                sx={{ 
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
            />
          </Grid>
        </Grid>
        </Card>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{ 
            borderRadius: 2,
            px: 3,
            py: 1,
            textTransform: 'none',
            fontWeight: 'bold',
            mr: 1
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={onSubmit} 
          variant="contained"
          sx={{ 
            borderRadius: 2,
            px: 3,
            py: 1,
            textTransform: 'none',
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #e085e8 0%, #e64a5a 100%)',
            }
          }}
        >
          Update Request
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Notification Dialog Component
const NotificationDialog = ({ open, onClose, request, form, setForm, onSubmit }) => {
  if (!request) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        }
      }}
    >
      {/* Custom Header */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        color: 'white',
        p: 3,
        borderRadius: '12px 12px 0 0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2 }}>
              <NotificationIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Send Notification
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {request.requestId}
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={onClose} 
            sx={{ 
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
            }}
          >
            <CancelIcon />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Card sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Notification Type</InputLabel>
              <Select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                label="Notification Type"
                  sx={{ borderRadius: 2 }}
              >
                <MenuItem value="status_update">Status Update</MenuItem>
                <MenuItem value="delivery_update">Delivery Update</MenuItem>
                <MenuItem value="custom">Custom Message</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Enter your notification message..."
                sx={{ 
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
            />
          </Grid>
          <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                Delivery Methods
              </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant={form.sendEmail ? 'contained' : 'outlined'}
                onClick={() => setForm({ ...form, sendEmail: !form.sendEmail })}
                size="small"
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    minWidth: 80
                  }}
              >
                Email
              </Button>
              <Button
                variant={form.sendSms ? 'contained' : 'outlined'}
                onClick={() => setForm({ ...form, sendSms: !form.sendSms })}
                size="small"
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    minWidth: 80
                  }}
              >
                SMS
              </Button>
            </Box>
          </Grid>
        </Grid>
        </Card>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{ 
            borderRadius: 2,
            px: 3,
            py: 1,
            textTransform: 'none',
            fontWeight: 'bold',
            mr: 1
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={onSubmit} 
          variant="contained" 
          startIcon={<SendIcon />}
          sx={{ 
            borderRadius: 2,
            px: 3,
            py: 1,
            textTransform: 'none',
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #3d8bfe 0%, #00d4fe 100%)',
            }
          }}
        >
          Send Notification
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AtmCardManagement;